import fetchWithAuth from "../utils/fetchWithAuth";
import { getEndpoints, buildEndpoint } from "../utils/ApiContext";

export const getUsers = async (groupname = null) => {
  try {
    let url = buildEndpoint("API_USERGET");

    if (groupname) {
      url += `?groupname=${encodeURIComponent(groupname)}`;
    }

    const response = await fetchWithAuth(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al obtener usuarios (${response.status}): ${errorText || response.statusText}`);
    }

    const users = await response.json();
    return users;
  } catch (error) {
    console.error("Error en getUsers:", error);
    throw error;
  }
};

export const getUserByUsername = async (username) => {
  try {
    if (!username) {
      throw new Error("El nombre del usuario es requerido");
    }

    await getEndpoints();
    const url = buildEndpoint('API_USERGET', username);

    const response = await fetchWithAuth(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 404) {
        throw new Error(`Usuario '${username}' no encontrado`);
      }
      throw new Error(`Error al obtener usuario (${response.status}): ${errorText || response.statusText}`);
    }

    const userData = await response.json();
    return userData;
  } catch (error) {
    console.error("Error en getUserByUsername:", error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    if (!userData || !userData.username || !userData.password) {
      throw new Error("El nombre de usuario y contraseña son requeridos");
    }

    await getEndpoints();
    const url = buildEndpoint("API_USERGET");

    const payload = {
      username: userData.username,
      password: userData.password
    };

    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorMessage = '';
      try {
        const errorJson = await response.json();
        errorMessage = errorJson.message || errorJson.error || errorJson.detail || '';
      } catch (jsonError) {
        errorMessage = await response.text();
      }

      if (response.status === 409) {
        throw new Error(`El usuario '${userData.username}' ya existe`);
      } else if (response.status === 400) {
        throw new Error(`Datos de usuario inválidos: ${errorMessage}`);
      }

      throw new Error(`Error al crear usuario (${response.status}): ${errorMessage || response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error en createUser:", error);
    throw error;
  }
};

export const updateUserApi = async (userData) => {
  try {
    if (!userData || !userData.username) {
      throw new Error("El nombre del usuario es requerido");
    }

    await getEndpoints();
    const url = buildEndpoint('API_USERGET', userData.username);

    const requestBody = {};

    if (userData.groups) {
      requestBody.groups = userData.groups;
    }

    const response = await fetchWithAuth(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorMessage = '';
      try {
        const errorJson = await response.json();
        errorMessage = errorJson.message || errorJson.error || errorJson.detail || '';
      } catch (jsonError) {
        errorMessage = await response.text();
      }

      if (response.status === 404) {
        throw new Error(`Usuario '${userData.username}' no encontrado`);
      } else if (response.status === 400) {
        throw new Error(`Datos inválidos: ${errorMessage}`);
      } else {
        throw new Error(`Error al actualizar usuario (${response.status}): ${errorMessage || response.statusText}`);
      }
    }

    if (response.status === 204) {
      return { success: true, message: 'Usuario actualizado correctamente' };
    }

    try {
      const result = await response.json();
      return result;
    } catch (e) {
      return { success: true, message: 'Usuario actualizado correctamente' };
    }
  } catch (error) {
    console.error('Error en updateUserApi:', error);
    throw error;
  }
};

export const deleteUserApi = async (username) => {
  try {
    if (!username) {
      throw new Error("El nombre del usuario es requerido");
    }

    await getEndpoints();
    const url = buildEndpoint('API_USERGET', username);

    const response = await fetchWithAuth(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorMessage = '';
      try {
        const errorJson = await response.json();
        errorMessage = errorJson.message || errorJson.error || errorJson.detail || '';
      } catch (jsonError) {
        errorMessage = await response.text();
      }

      if (response.status === 404) {
        throw new Error(`Usuario '${username}' no encontrado`);
      }

      throw new Error(`Error al eliminar usuario (${response.status}): ${errorMessage || response.statusText}`);
    }

    if (response.status === 204) {
      return { success: true, message: 'Usuario eliminado correctamente' };
    }

    try {
      const result = await response.json();
      return result;
    } catch (e) {
      return { success: true, message: 'Usuario eliminado correctamente' };
    }
  } catch (error) {
    console.error('Error en deleteUserApi:', error);
    throw error;
  }
};

export const searchUsers = async (criteria = {}) => {
  try {
    let users = await getUsers(criteria.group);

    if (criteria.username) {
      users = users.filter(user =>
        user.username.toLowerCase().includes(criteria.username.toLowerCase())
      );
    }

    if (criteria.email) {
      users = users.filter(user =>
        (user.email || '').toLowerCase().includes(criteria.email.toLowerCase())
      );
    }

    if (criteria.enabled !== undefined) {
      users = users.filter(user => user.enabled === criteria.enabled);
    }

    return users;
  } catch (error) {
    console.error("Error en searchUsers:", error);
    throw error;
  }
};

export const getUserStats = async () => {
  try {
    const users = await getUsers();

    const stats = {
      totalUsers: users.length,
      enabledUsers: users.filter(user => user.enabled !== false).length,
      disabledUsers: users.filter(user => user.enabled === false).length,
      usersWithEmail: users.filter(user => user.email && user.email.trim() !== '').length,
      usersWithoutEmail: users.filter(user => !user.email || user.email.trim() === '').length,
    };

    return stats;
  } catch (error) {
    console.error("Error en getUserStats:", error);
    throw error;
  }
};

export const validateUserData = (userData, isUpdate = false) => {
  const errors = [];

  if (!isUpdate && (!userData.username || userData.username.trim() === '')) {
    errors.push('El nombre de usuario es requerido');
  } else if (userData.username && userData.username.trim().length < 3) {
    errors.push('El nombre de usuario debe tener al menos 3 caracteres');
  }

  if (!isUpdate && (!userData.password || userData.password.trim() === '')) {
    errors.push('La contraseña es requerida');
  } else if (userData.password && userData.password.length < 8) {
    errors.push('La contraseña debe tener al menos 8 caracteres');
  }

  if (userData.email && userData.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      errors.push('El formato del email no es válido');
    }
  }

  if (userData.groups && typeof userData.groups === 'object') {
    const groupKeys = Object.keys(userData.groups);

    const invalidValues = groupKeys.filter(key =>
      typeof userData.groups[key] !== 'boolean'
    );

    if (invalidValues.length > 0) {
      errors.push('Los valores de grupos deben ser booleanos (true/false)');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Manages the addition or removal of multiple users from a group by sending a PUT request to the API.
 * The function constructs a request body that includes an optional group description and an object
 * mapping usernames to a boolean action (true for 'add', false for 'remove').
 * * @param {string[]} usernames - An array of usernames to be added to or removed from the group. Must not be empty.
 * @param {string} groupname - The name of the group to be modified. Must be a non-empty string.
 * @param {'add' | 'remove'} action - The action to perform on the users. Use 'add' to add them to the group or 'remove' to remove them.
 * @param {string} [description] - An optional description for the group. If provided, it will be included in the request body.
 * @returns {Promise<void>} A promise that resolves when the request is successful.
 * @throws {Error} If required parameters are missing or invalid, or if the API request fails.
 * 
 * @example
 * // Add users 'john_doe' and 'jane_smith' to the 'admin' group with a description.
 * manageUsersInGroup(['john_doe', 'jane_smith'], 'admin', 'add', 'A team for admins.');
 *
 * @example
 * // Remove the user 'john_doe' from the 'admin' group without a description.
 * // The fourth parameter (description) can be omitted.
 * manageUsersInGroup(['john_doe'], 'admin', 'remove');
 */
export const manageUsersInGroup = async (
  usernames,
  groupname,
  action,
  description
) => {
  try {
    if (!Array.isArray(usernames) || usernames.length === 0 || !groupname || !action) {
      throw new Error("Usernames (as an array), group name, and action are required.");
    }

    if (action !== "add" && action !== "remove") {
      throw new Error("Invalid action provided. Use 'add' or 'remove'.");
    }

    const actionValue = action === "add";
    const usersObject = usernames.reduce((acc, username) => {
      acc[username] = actionValue;
      return acc;
    }, {});

    const groupData = {
      ...(description && {
        description
      }),
      users: usersObject
    };

    console.log(JSON.stringify(groupData))

    const url = buildEndpoint('API_GROUPS', groupname);
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body  : JSON.stringify(groupData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    return;

  } catch (error) {
    console.error('Error en manageUsersInGroup:', error);
    throw error;
  }
};

export const updateUserGroups = async (username, groupChanges) => {
  if (!username || !groupChanges || typeof groupChanges !== 'object') {
    throw new Error("El nombre del usuario y los cambios de grupos son requeridos");
  }

  return updateUserApi({
    username: username,
    groups: groupChanges
  });
};

export const postUserApi = updateUserApi;
export const putUserApi = updateUserApi;

export async function getFetchBaseUrl() {
  try {
    return await getEndpoints();
  } catch (err) {
    console.error("Error al obtener la configuración de la API:", err);
    throw err;
  }
}